import React from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Divider from "@mui/material/Divider";
import ListItemText from "@mui/material/ListItemText";
import Button from "@mui/material/Button";
import { useState, useEffect } from "react";
import arrowIconBottom from "./../../../../../assets/_ArrowIconBottom_.svg";
import closeIconModal from "./../../../../../assets/_CloseIconModal_.svg";

import Modal from "@mui/material/Modal";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormHelperText from "@mui/material/FormHelperText";
import Chip from "@mui/material/Chip";
import { Switch } from "@mui/base/Switch";
import { ButtonDeleted } from "../../../../../components/ButtonDeleted";
import { ButtonEdited } from "../../../../../components/ButtonEdited";
import { useConfirm } from "../../../../../hooks/useConfirm";
import { Button as ButtonCustom } from "../../../../../components/Button";
import addCircleImage from "./../../../../../assets/_AddCircleIcon_.svg";
import { toast } from "react-toastify";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  addOrUpdateProjectCustomField,
  deleteProjectCustomField,
  getAllProjectsCustomField,
} from "../../../../../services/api/projectCustomField/projectCustomFieldService";
import { ICustomField } from "../../../../../@types/customField.type";
import {
  addOrUpdateCustomerCustomField,
  deleteCustomerCustomField,
  getAllCustomersCustomField,
} from "../../../../../services/api/customerCustomField/customerCustomFieldService";
import styles from "./styles.module.scss";
import {
  addOrUpdateResourceCustomField,
  deleteResourceCustomField,
  getAllResourceCustomField,
} from "../../../../../services/api/resourceCustomField/resourceCustomFieldService";

interface FormAndListCustomFieldsProps {
  page: "projects" | "customers" | "resources";
}

interface ChipListOptionItem {
  key: string;
  label: string;
}

const schema = z.object({
  nameField: z.string().trim().min(1, { message: "O nome é obrigatório." }),
  typeField: z.string().min(1, { message: "O tipo é obrigatório." }),
});

type FormValues = z.infer<typeof schema>;

export const FormAndListCustomFields: React.FC<
  FormAndListCustomFieldsProps
> = ({ page }) => {
  const [selectedCustomFieldTypeId, setSelectedCustomFieldTypeId] = useState<
    string | undefined
  >();
  const [listing, setListing] = useState<ICustomField[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [isMandatory, setIsMandatory] = useState(false);
  const [isVisibleInGrid, setIsVisibleInGrid] = useState(false);
  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const displayedList = showAll ? listing : listing?.slice(0, 3);
  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const handleOpenModalConfirm = () => setOpenModalConfirm(true);
  const handleCloseModalConfirm = () => setOpenModalConfirm(false);
  const [reload, setReload] = useState(true);
  const [isErrorListType, setIsErrorListType] = useState(false);
  const [chipOptionText, setChipOptionText] = useState("");
  const [chipListOption, setChipListOption] = useState<
    readonly ChipListOptionItem[]
  >([]);

  const {
    handleSubmit,
    watch,
    control,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      nameField: "",
      typeField: undefined,
    },
  });

  const booleanValuesForYesOrNo = {
    true: "Sim",
    false: "Não",
  };

  const mapFieldTypeOptionsValue: Record<string, string> = {
    "093bdd03-1dcb-4f0c-b44a-6f78cbe16691": "Texto",
    "1fda1cb9-6aed-45d3-a518-5cdb051ccf25": "Lista",
    "234d101a-de4a-4fb5-b0c8-4eb3415d909a": "Texto Multlinhas",
    "cf834122-eb8b-4360-a372-b7f86cd7035b": "Data",
    "6c429eaa-7ad6-41e3-be70-b38809c83d87": "Booleano",
  };

  const mapFieldTypeOptionsId: Record<string, string> = {
    texto: "093bdd03-1dcb-4f0c-b44a-6f78cbe16691",
    lista: "1fda1cb9-6aed-45d3-a518-5cdb051ccf25",
    "texto multlinhas": "234d101a-de4a-4fb5-b0c8-4eb3415d909a",
    data: "cf834122-eb8b-4360-a372-b7f86cd7035b",
    booleano: "6c429eaa-7ad6-41e3-be70-b38809c83d87",
  };

  useEffect(() => {
    const fetchList = async () => {
      try {
        setLoading(true);
        let response;

        if (page === "projects") {
          response = await getAllProjectsCustomField();
        } else if (page === "customers") {
          response = await getAllCustomersCustomField();
        } else {
          response = await getAllResourceCustomField();
        }

        if (response.statusCode !== 200) {
          throw new Error(response.message || "Erro ao buscar os campos");
        }

        if (page === "resources") {
          const reversedResult = [...response.result]?.reverse() || [];
          setListing(reversedResult);
        } else {
          const reversedResult = [...response.result.items]?.reverse() || [];
          setListing(reversedResult);
        }

        if (response.result.items?.length === 0) {
          handleCloseModal();
        }
      } catch (error: unknown) {
        if (error instanceof Error) {
          console.log(error.message);
        } else {
          console.log("Ocorreu um erro inesperado");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchList();
  }, [reload]);

  const { showConfirm, Confirm } = useConfirm(
    "Tem certeza que deseja excluir o campo personalizado?",
    openModalConfirm,
    handleCloseModalConfirm
  );

  const handleDelete = async (customFieldId: string) => {
    handleOpenModalConfirm();
    try {
      const isConfirm = await showConfirm();
      if (isConfirm) {
        if (page === "projects") {
          await deleteProjectCustomField(customFieldId);
        } else if (page === "customers") {
          await deleteCustomerCustomField(customFieldId);
        } else {
          await deleteResourceCustomField(customFieldId);
        }

        setReload((prev) => !prev);
        toast.success("Campo personalizado excluído com sucesso!");
      }
    } catch (error) {
      console.error("Erro ao deletar:", error);
    }
  };

  const handleDeleteOptionItem = (chipToDelete: ChipListOptionItem) => () => {
    setChipListOption((chips) => {
      return chips.filter((chip) => chip.key !== chipToDelete.key);
    });
  };

  const handleUpdate = (customField: ICustomField) => {
    setSelectedCustomFieldTypeId(customField?.id);
    reset({
      nameField: customField.name,
      typeField: mapFieldTypeOptionsValue[customField.customFieldTypeId],
    });
    setIsMandatory(customField.required);
    setIsVisibleInGrid(customField.showInList);

    setChipListOption(() => {
      return (
        customField.listOptions?.split(",").map((nameItem, index) => {
          return {
            key: new Date().toISOString() + index,
            label: nameItem,
          };
        }) || []
      );
    });

    handleCloseModal();
  };

  const addOptionItemList = () => {
    if (chipOptionText.trim() === "") return;

    const dividerRegex = /[,\-_/|\\]+/;

    if (dividerRegex.test(chipOptionText)) {
      const listInput = chipOptionText
        .split(dividerRegex)
        .filter((item) => item.trim() !== "")
        .map((item, index) => {
          return {
            key: new Date().toISOString() + index,
            label: item,
          };
        });

      setChipListOption((chips) => {
        return [...chips, ...listInput];
      });
    } else {
      const newItem = {
        key: new Date().toISOString(),
        label: chipOptionText,
      };

      setChipListOption((chips) => {
        return [...chips, newItem];
      });
    }

    setChipOptionText("");
    setIsErrorListType(false);
  };

  const typeField = watch("typeField");

  const resetFields = () => {
    setSelectedCustomFieldTypeId(undefined);
    reset({
      nameField: "",
      typeField: "",
    });
    setIsMandatory(true);
    setIsVisibleInGrid(true);
    setChipListOption([]);
  };

  const onSubmit = async (data: FormValues) => {
    if (typeField === "Lista" && chipListOption.length === 0) {
      setIsErrorListType(true);
      return;
    }

    let dataFromApi: ICustomField;

    if (selectedCustomFieldTypeId) {
      dataFromApi = {
        id: selectedCustomFieldTypeId,
        customFieldTypeId:
          mapFieldTypeOptionsId[`${data.typeField.toLowerCase()}`] || "",
        name: data.nameField,
        required: isMandatory,
        showInList: isVisibleInGrid,
        listOptions: chipListOption.map((item) => item.label).join(","),
      };
    } else {
      dataFromApi = {
        customFieldTypeId:
          mapFieldTypeOptionsId[`${data.typeField.toLowerCase()}`] || "",
        name: data.nameField,
        required: isMandatory,
        showInList: isVisibleInGrid,
        listOptions: chipListOption.map((item) => item.label).join(","),
      };
    }

    try {
      let response;

      if (page === "projects") {
        response = await addOrUpdateProjectCustomField(dataFromApi);
      } else if (page === "customers") {
        response = await addOrUpdateCustomerCustomField(dataFromApi);
      } else {
        response = await addOrUpdateResourceCustomField(dataFromApi);
      }

      if (response.statusCode === 200) {
        resetFields();
        toast.success("Campo personalizado salvo com sucesso!");
        setReload(!reload);
      } else {
        toast.error(
          "Não foi possível salvar o Campo personalizado. Tente novamente mais tarde!"
        );
      }
    } catch (error) {
      toast.error(
        "Não foi possível salvar o Campo personalizado. Tente novamente mais tarde!"
      );
      if (error instanceof Error) {
        console.log(error.message);
      } else {
        console.log("Ocorreu um erro inesperado");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.formAndListCustomFields}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={styles.formCustomFields}
      >
        <h3 className={styles.title}>Configurações dos Campos</h3>
        <div className={styles.fieldsRow}>
          <Controller
            name="nameField"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                label="Nome do Campo"
                placeholder={"Digite o nome do campo personalizado"}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{ maxLength: 100 }}
                value={field.value}
                onChange={field.onChange}
                error={!!errors.nameField}
                helperText={errors.nameField?.message}
                fullWidth
              />
            )}
          />

          <FormControl fullWidth>
            <InputLabel shrink id="type-field-label">
              Tipo de Campo
            </InputLabel>
            <Controller
              name="typeField"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Select
                  {...field}
                  label="Tipo de Campo"
                  onChange={(e) => {
                    setChipListOption([]);
                    field.onChange(e.target.value);
                  }}
                  displayEmpty
                  error={!!errors.typeField}
                >
                  <MenuItem value="">Selecione...</MenuItem>
                  <MenuItem value="Texto">Texto</MenuItem>
                  <MenuItem value="Texto Multlinhas">Texto Multlinhas</MenuItem>
                  <MenuItem value="Lista">Lista</MenuItem>
                  <MenuItem value="Data">Data</MenuItem>
                  <MenuItem value="Booleano">Booleano (Sim ou Não)</MenuItem>
                </Select>
              )}
            />
            <FormHelperText style={{ color: "#d32f2f" }}>
              {errors.typeField?.message}
            </FormHelperText>
          </FormControl>
          <div className={styles.containerSwitchs}>
            <div className={styles.switchContainer}>
              <p>Obrigatoriedade</p>
              <div className={styles.switchMandatory}>
                <label className={`${isMandatory && styles.activeLabel}`}>
                  Sim
                </label>
                <Switch
                  className={styles.switchRoot}
                  checked={isMandatory}
                  onChange={(event: any) =>
                    setIsMandatory(event.target.checked)
                  }
                />
                <label className={`${!isMandatory && styles.activeLabel}`}>
                  Não
                </label>
              </div>
            </div>

            <div className={styles.switchContainer}>
              <p>Visível na lista</p>
              <div className={styles.switchVisibleInGrid}>
                <label className={`${isVisibleInGrid && styles.activeLabel}`}>
                  Sim
                </label>
                <Switch
                  className={styles.switchRoot}
                  checked={isVisibleInGrid}
                  onChange={(event: any) =>
                    setIsVisibleInGrid(event.target.checked)
                  }
                />
                <label className={`${!isVisibleInGrid && styles.activeLabel}`}>
                  Não
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.fieldsRow}></div>
        {typeField === "Lista" && (
          <>
            <div className={styles.fieldsRow}>
              <TextField
                fullWidth
                label="Itens da Lista"
                placeholder={"Digite as opções da lista"}
                InputLabelProps={{
                  shrink: true,
                }}
                value={chipOptionText}
                onChange={(event) => setChipOptionText(event.target.value)}
              />

              <ButtonCustom
                iconUrl={addCircleImage}
                onClick={() => addOptionItemList()}
                content="Novo item na lista"
                type="button"
                className={styles.button}
              />
            </div>
            <div
              className={`${styles.listOptionItems} ${
                isErrorListType && styles.errorMsg
              }`}
            >
              {chipListOption.length > 0 ? (
                chipListOption.map((data) => {
                  return (
                    <ListItem key={data.key}>
                      <Chip
                        label={data.label}
                        onDelete={handleDeleteOptionItem(data)}
                      />
                    </ListItem>
                  );
                })
              ) : (
                <>
                  <div className={`${styles.msgNoItens}`}>
                    <span className={styles.primaryText}>
                      Não existem itens na lista
                    </span>
                    <span className={styles.secondaryText}>
                      É possível adicionar no campo a cima
                    </span>
                  </div>
                  {isErrorListType && (
                    <p style={{ color: "#d32f2f", margin: "auto" }}>
                      Adicione pelo menos um item na lista de campos.
                    </p>
                  )}
                </>
              )}
            </div>
          </>
        )}
        <Button
          variant="contained"
          color="primary"
          type="submit"
          className={styles.btnsSave}
          disabled={loading}
        >
          Salvar Personalizações
        </Button>
      </form>
      <div className={styles.listCustomFields}>
        <h3 className={styles.title}>Meus Campos</h3>
        <List className={styles.listContainer}>
          <div className={styles.listContainerNotSeeMore}>
            <ListItem
              alignItems="center"
              className={`${styles.listItem} ${styles.listItemHeader}`}
            >
              <ListItemText
                className={styles.listItemText}
                primary={<span className={styles.primaryText}>Nome</span>}
              />

              <ListItemText
                className={styles.listItemText}
                primary={<span className={styles.primaryText}>Tipo</span>}
              />

              <ListItemText
                className={styles.listItemText}
                primary={
                  <span className={styles.primaryText}>Obrigatoriedade</span>
                }
              />

              <ListItemText
                className={styles.listItemText}
                primary={
                  <span className={styles.primaryText}>Visível na lista</span>
                }
              />
              <ListItemText
                className={styles.listItemText}
                style={{ maxWidth: "96px" }}
                primary={<span className={styles.primaryText}></span>}
              />
            </ListItem>
            {loading ? (
              <ListItem alignItems="center" className={styles.listItem}>
                <p style={{ textAlign: "center", margin: "auto" }}>
                  Carregando...
                </p>
              </ListItem>
            ) : displayedList?.length === 0 ||
              !displayedList ||
              displayedList?.every(
                (customField) => customField.id === undefined
              ) ? (
              <ListItem alignItems="center" className={styles.listItem}>
                <ListItemText
                  className={styles.listItemText}
                  primary={
                    <span className={styles.primaryText}>
                      Não existem campos personalizados
                    </span>
                  }
                  secondary={
                    <span className={styles.secondaryText}>
                      É possível adicionar no formulário a cima
                    </span>
                  }
                  style={{ textAlign: "center" }}
                />
              </ListItem>
            ) : (
              displayedList?.map((customField, index) => (
                <React.Fragment key={customField.id}>
                  <ListItem alignItems="center" className={styles.listItem}>
                    <ListItemText
                      className={styles.listItemText}
                      primary={
                        <span className={styles.primaryText}>
                          {customField.name}
                        </span>
                      }
                    />

                    <ListItemText
                      className={styles.listItemText}
                      primary={
                        <span className={styles.primaryText}>
                          {
                            mapFieldTypeOptionsValue[
                              `${customField.customFieldTypeId}`
                            ]
                          }
                        </span>
                      }
                    />

                    <ListItemText
                      className={styles.listItemText}
                      primary={
                        <span className={styles.primaryText}>
                          {booleanValuesForYesOrNo[`${customField.required}`]}
                        </span>
                      }
                    />

                    <ListItemText
                      className={styles.listItemText}
                      primary={
                        <span className={styles.primaryText}>
                          {booleanValuesForYesOrNo[`${customField.showInList}`]}
                        </span>
                      }
                    />
                    <div style={{ display: "flex", gap: "1rem" }}>
                      <ButtonDeleted
                        handleDeleteItem={() =>
                          customField.id && handleDelete(customField.id)
                        }
                      />

                      <ButtonEdited
                        handleEditeItem={() => handleUpdate(customField)}
                      />
                    </div>
                  </ListItem>
                  {listing.length > 3 ? (
                    <Divider
                      variant="middle"
                      component="li"
                      className={styles.divider}
                    />
                  ) : (
                    index < displayedList.length - 1 && (
                      <Divider
                        variant="middle"
                        component="li"
                        className={styles.divider}
                      />
                    )
                  )}
                </React.Fragment>
              ))
            )}
          </div>

          {displayedList?.length !== 0 &&
            listing?.length > 3 &&
            displayedList?.every(
              (customField) => customField.id !== undefined
            ) && (
              // Exibe o botão "Ver Mais" apenas se houver lista
              <ListItem
                style={{ justifyContent: "center" }}
                className={styles.listItemSeeMore}
              >
                <Button
                  onClick={handleOpenModal}
                  variant="text"
                  color="primary"
                  sx={{ textTransform: "none", padding: "10px 8px" }}
                  className={styles.buttonSeeMore}
                >
                  <div>Ver Mais</div>
                  {arrowIconBottom && (
                    <img src={arrowIconBottom} style={{ marginLeft: "8px" }} />
                  )}
                </Button>
              </ListItem>
            )}
        </List>
      </div>

      <Modal open={openModal} onClose={handleCloseModal}>
        <div className={styles.modal}>
          <div className={styles.header}>
            <h3>Todos os Campos Selecionados</h3>
            <Button onClick={handleCloseModal} className={styles.iconButton}>
              {closeIconModal && <img src={closeIconModal} />}
            </Button>
          </div>
          <List className={styles.listContainerModal}>
            <ListItem
              alignItems="center"
              className={`${styles.listItemModal} ${styles.listItemHeader}`}
            >
              <ListItemText
                className={styles.listItemText}
                primary={<span className={styles.primaryText}>Nome</span>}
              />

              <ListItemText
                className={styles.listItemText}
                primary={<span className={styles.primaryText}>Tipo</span>}
              />

              <ListItemText
                className={styles.listItemText}
                primary={
                  <span className={styles.primaryText}>Obrigatoriedade</span>
                }
              />

              <ListItemText
                className={styles.listItemText}
                primary={
                  <span className={styles.primaryText}>Visível na lista</span>
                }
              />
              <ListItemText
                className={styles.listItemText}
                style={{ maxWidth: "96px" }}
                primary={<span className={styles.primaryText}></span>}
              />
            </ListItem>
            <div className={styles.listBody}>
              {listing?.map((customField) => (
                <React.Fragment key={customField.id}>
                  <ListItem
                    alignItems="center"
                    className={styles.listItemModal}
                  >
                    <ListItemText
                      className={styles.listItemText}
                      primary={
                        <span className={styles.primaryText}>
                          {customField.name}
                        </span>
                      }
                    />

                    <ListItemText
                      className={styles.listItemText}
                      primary={
                        <span className={styles.primaryText}>
                          {mapFieldTypeOptionsValue[
                            `${customField.customFieldTypeId}`
                          ] || ""}
                        </span>
                      }
                    />

                    <ListItemText
                      className={styles.listItemText}
                      primary={
                        <span className={styles.primaryText}>
                          {booleanValuesForYesOrNo[`${customField.required}`]}
                        </span>
                      }
                    />

                    <ListItemText
                      className={styles.listItemText}
                      primary={
                        <span className={styles.primaryText}>
                          {booleanValuesForYesOrNo[`${customField.showInList}`]}
                        </span>
                      }
                    />
                    <ButtonDeleted
                      handleDeleteItem={() =>
                        customField.id && handleDelete(customField.id)
                      }
                    />
                    <ButtonEdited
                      handleEditeItem={() => handleUpdate(customField)}
                    />
                  </ListItem>
                  <Divider
                    variant="middle"
                    component="li"
                    className={styles.divider}
                  />
                </React.Fragment>
              ))}
            </div>
          </List>
        </div>
      </Modal>
      {Confirm}
    </div>
  );
};
